import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Eye, EyeOff, CheckCircle2 } from "lucide-react";

// Phase IM — admin / manager magic-link landing page. Public route
// mounted at /claim-invite/:token. On success, captures the JWT from the
// claim endpoint and routes the user to their role-appropriate landing
// page (admins → /admin, managers → /manager).

const REASON_COPY = {
    not_found: {
        title: "We can't find this invitation.",
        body: "It may have been issued from a different environment (production vs preview), or it has been replaced by a newer invite. Ask the admin to send you a fresh invitation.",
    },
    expired: {
        title: "This invitation has expired.",
        body: "Invitation links are valid for 7 days. Ask the admin to re-send a fresh invitation.",
    },
    revoked: {
        title: "This invitation was replaced by a newer one.",
        body: "Check your email for the most recent invitation, or ask the admin to re-issue.",
    },
    already_used: {
        title: "This invitation has already been used.",
        body: "If you've forgotten your password, use \"Reset password\" on the sign-in page. Otherwise, ask for a fresh invitation.",
    },
};

function InvalidLink({ reason }) {
    const copy = REASON_COPY[reason] || REASON_COPY.not_found;
    return (
        <AuthLayout
            eyebrow="Invitation"
            title={copy.title}
            subtitle={copy.body}
        >
            <div
                data-testid="claim-invite-error"
                className="rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2"
            >
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                    Need help? Contact the super admin who invited you, or
                    email <a className="underline" href="mailto:support@rentflow.app">support@rentflow.app</a>.
                </span>
            </div>
        </AuthLayout>
    );
}

export default function ClaimInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState(null);
    const [invalidReason, setInvalidReason] = useState(null);
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.get(`/auth/claim-invite/${token}`);
                if (active) setInvite(data);
            } catch (err) {
                const reason = err?.response?.data?.detail?.reason || "not_found";
                if (active) setInvalidReason(reason);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (newPwd !== confirmPwd) {
            setError("Passwords do not match");
            return;
        }
        if (newPwd.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/auth/claim-invite/${token}`, { new_password: newPwd });
            // Persist the session token, hydrate auth context, redirect by role.
            localStorage.setItem("rp_token", data.token);
            setUser(data.user);
            toast.success("Welcome aboard — you're signed in.");
            const role = data.user.role;
            if (role === "manager") {
                navigate("/manager", { replace: true });
            } else {
                navigate("/admin", { replace: true });
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail?.reason) {
                setInvalidReason(detail.reason);
            } else if (typeof detail === "string") {
                setError(detail);
            } else if (err?.response?.status === 422) {
                setError("Password must be at least 8 characters");
            } else {
                setError("Could not activate your account. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <AuthLayout eyebrow="Invitation" title="Checking your invite…" subtitle="One moment.">
                <div className="text-sm text-slate-500" data-testid="claim-invite-loading">Loading…</div>
            </AuthLayout>
        );
    }

    if (invalidReason) {
        return <InvalidLink reason={invalidReason} />;
    }

    const roleLabel = invite.role === "manager" ? "manager" : "admin";

    return (
        <AuthLayout
            eyebrow="Activate your account"
            title={`Welcome, ${invite.full_name}.`}
            subtitle={`You've been invited as ${roleLabel === "manager" ? "a manager" : "an admin"}. Set a password to activate your account.`}
        >
            <form onSubmit={onSubmit} className="space-y-5" data-testid="claim-invite-form">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-700" />
                    <span>
                        Activating: <strong className="text-slate-900">{invite.email}</strong>
                    </span>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="claim_new_password">New password</Label>
                    <div className="relative">
                        <Input
                            id="claim_new_password"
                            data-testid="claim-invite-new-password"
                            type={showPwd ? "text" : "password"}
                            autoComplete="new-password"
                            required
                            minLength={8}
                            value={newPwd}
                            onChange={(e) => setNewPwd(e.target.value)}
                            className="pr-10"
                        />
                        <button
                            type="button"
                            data-testid="claim-invite-toggle-visibility"
                            onClick={() => setShowPwd((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            aria-label={showPwd ? "Hide password" : "Show password"}
                        >
                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                    <p className="text-xs text-slate-500">At least 8 characters.</p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="claim_confirm_password">Confirm password</Label>
                    <Input
                        id="claim_confirm_password"
                        data-testid="claim-invite-confirm-password"
                        type={showPwd ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={confirmPwd}
                        onChange={(e) => setConfirmPwd(e.target.value)}
                    />
                </div>

                {error && (
                    <div
                        data-testid="claim-invite-form-error"
                        className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                    >
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    data-testid="claim-invite-submit"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white h-11"
                >
                    {submitting ? "Activating…" : "Set password and sign in"}
                </Button>
            </form>
        </AuthLayout>
    );
}
